import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { PushTemplate, RunTrigger, type AssistantTestMemberDto, type AssistantTestPushTicketDto, type AssistantTestReceiptDto } from '@signalvote/shared';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  useAssistantTestCampaigns,
  useAssistantTestDeleteNote,
  useAssistantTestPurchase,
  useAssistantTestEditNote,
  useAssistantTestMembers,
  useAssistantTestPush,
  useAssistantTestReceipts,
  useAssistantTestReset,
  useAssistantTestRun,
} from '@/hooks/queries';
import { FilterSelect } from './FilterSelect';
import { purchaseOutcomeLines } from './telemetryDisplay';

const TEMPLATES = Object.values(PushTemplate);
const RESET_PARTS = ['chat', 'notes', 'wakeups', 'preferences'] as const;
type ResetPart = (typeof RESET_PARTS)[number];

const when = (iso: string) => new Date(iso).toLocaleString();

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-border p-4 space-y-3">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      {children}
    </div>
  );
}

function Result({ error, children }: { error?: Error | null; children?: React.ReactNode }) {
  if (error) return <p className="text-sm text-destructive">{error.message}</p>;
  return children ? <div className="text-sm text-muted-foreground">{children}</div> : null;
}

function Runs({ campaignId, member }: { campaignId: string; member: AssistantTestMemberDto }) {
  const { mutate, data, error, isPending, variables } = useAssistantTestRun();
  const start = (trigger: RunTrigger) => mutate({ campaignId, userId: member.userId, trigger });
  return (
    <Section title="Background runs">
      <p className="text-xs text-muted-foreground">
        Starts now, past the daily cap, quiet hours and the run gap, whatever the scheduler flag says.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" disabled={isPending || !member.isPlanner} onClick={() => start(RunTrigger.DailyFloor)}>
          Run the morning brief now
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={isPending || !member.isPlanner || member.pendingWakeups.length === 0}
          onClick={() => start(RunTrigger.Wakeup)}
        >
          Fire {member.pendingWakeups.length} wake-up{member.pendingWakeups.length === 1 ? '' : 's'} now
        </Button>
      </div>
      {member.pendingWakeups.length > 0 && (
        <ul className="text-xs text-muted-foreground space-y-1">
          {member.pendingWakeups.map((w) => (
            <li key={w.dueAt + w.reason}>
              {when(w.dueAt)}: {w.reason || '(no reason)'}
            </li>
          ))}
        </ul>
      )}
      <Result error={error}>
        {data && (
          <>
            {variables?.trigger === RunTrigger.Wakeup ? `Claimed ${data.claimedWakeups} wake-ups. ` : 'Brief started. '}
            The reply lands in their thread in under a minute.{' '}
            <Link to="/admin/assistant-telemetry/runs/$runId" params={{ runId: data.runId }} className="underline">
              Open the run
            </Link>
          </>
        )}
      </Result>
    </Section>
  );
}

function Push({ campaignId, member }: { campaignId: string; member: AssistantTestMemberDto }) {
  const [template, setTemplate] = useState<PushTemplate>(PushTemplate.PledgeOverdue);
  const push = useAssistantTestPush();
  const receipts = useAssistantTestReceipts();
  const tickets: AssistantTestPushTicketDto[] = push.data ?? [];
  const ids = tickets.map((t) => t.ticketId).filter((id): id is string => id !== null);
  const receiptFor = (id: string | null): AssistantTestReceiptDto | undefined => receipts.data?.find((r) => r.ticketId === id);
  return (
    <Section title={`Push (${member.devices} phone${member.devices === 1 ? '' : 's'} registered)`}>
      <div className="flex flex-wrap items-end gap-2">
        <FilterSelect
          label="Template"
          value={template}
          onChange={(v) => v && setTemplate(v)}
          options={TEMPLATES.map((t) => ({ value: t, label: t }))}
        />
        <Button
          size="sm"
          disabled={push.isPending || member.devices === 0}
          onClick={() => {
            receipts.reset();
            push.mutate({ campaignId, userId: member.userId, template });
          }}
        >
          Send test push
        </Button>
        <Button size="sm" variant="outline" disabled={ids.length === 0 || receipts.isPending} onClick={() => receipts.mutate(ids)}>
          Check delivery
        </Button>
      </div>
      <Result error={push.error ?? receipts.error}>
        {tickets.length > 0 && (
          <ul className="space-y-1">
            {tickets.map((t, i) => {
              const receipt = receiptFor(t.ticketId);
              return (
                <li key={t.ticketId ?? i}>
                  {t.platform} (seen {when(t.lastSeenAt)}):{' '}
                  {t.ticketId ? 'Expo accepted' : `Expo refused: ${t.error}`}
                  {receipt && `, ${receipt.status}${receipt.error ? ` (${receipt.error})` : ''}`}
                </li>
              );
            })}
            {ids.length > 0 && !receipts.data && <li className="text-xs">Apple and Google answer in about a minute; then check delivery.</li>}
          </ul>
        )}
      </Result>
    </Section>
  );
}

function Purchase({ campaignId, member }: { campaignId: string; member: AssistantTestMemberDto }) {
  const [amount, setAmount] = useState('85.40');
  const [description, setDescription] = useState('STAPLES 00123 MOBILE AL');
  const { mutate, data, error, isPending } = useAssistantTestPurchase();
  const receipts = useAssistantTestReceipts();
  const ticketIds = data?.push?.ticketIds ?? [];
  const parsed = Number(amount);
  return (
    <Section title="Simulate a purchase">
      <p className="text-xs text-muted-foreground">
        Adds a money-out bank line to this campaign's ledger as a posted bank charge, then runs the charge check right away,
        whatever the scheduler flag says: each purchaser (or the finance managers when there are none) gets a card in their
        thread, and this member's push goes now, under their daily cap and quiet hours. Open the ledger with{' '}
        <code>?live=1</code> to watch it confirm.
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <label className="space-y-1 text-xs text-muted-foreground">
          <span>Amount ($)</span>
          <Input className="w-28" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
        </label>
        <label className="space-y-1 text-xs text-muted-foreground flex-1 min-w-48">
          <span>Bank description</span>
          <Input value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
        <Button
          size="sm"
          disabled={isPending || !(parsed > 0) || description.trim() === ''}
          onClick={() => {
            receipts.reset();
            mutate({ campaignId, userId: member.userId, amount: parsed, description, date: null });
          }}
        >
          Make the purchase
        </Button>
        <Button size="sm" variant="outline" disabled={ticketIds.length === 0 || receipts.isPending} onClick={() => receipts.mutate(ticketIds)}>
          Check delivery
        </Button>
      </div>
      <Result error={error ?? receipts.error}>
        {data && (
          <ul className="space-y-1">
            {purchaseOutcomeLines(data, member.userId).map((line) => (
              <li key={line}>{line}</li>
            ))}
            {receipts.data?.map((r) => (
              <li key={r.ticketId}>
                Delivery: {r.status}
                {r.error ? ` (${r.error})` : ''}
              </li>
            ))}
          </ul>
        )}
      </Result>
    </Section>
  );
}

function Notes({ campaignId, member }: { campaignId: string; member: AssistantTestMemberDto }) {
  const [editing, setEditing] = useState<{ id: string; text: string }>();
  const edit = useAssistantTestEditNote();
  const remove = useAssistantTestDeleteNote();
  return (
    <Section title={`Notes Scout keeps (${member.notes.length})`}>
      {member.notes.length === 0 && <p className="text-sm text-muted-foreground">None.</p>}
      <ul className="space-y-2">
        {member.notes.map((n) => (
          <li key={n.id} className="flex items-start gap-2 text-sm">
            <span className="text-xs text-muted-foreground w-28 shrink-0 pt-1">{n.kind}</span>
            {editing?.id === n.id ? (
              <>
                <Input value={editing.text} onChange={(e) => setEditing({ id: n.id, text: e.target.value })} />
                <Button
                  size="sm"
                  disabled={edit.isPending}
                  onClick={() => edit.mutate({ id: n.id, campaignId, text: editing.text }, { onSuccess: () => setEditing(undefined) })}
                >
                  Save
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(undefined)}>
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <span className="flex-1 text-foreground pt-1">{n.text}</span>
                <Button size="sm" variant="ghost" onClick={() => setEditing({ id: n.id, text: n.text })}>
                  Edit
                </Button>
                <Button size="sm" variant="ghost" disabled={remove.isPending} onClick={() => remove.mutate({ id: n.id, campaignId })}>
                  Delete
                </Button>
              </>
            )}
          </li>
        ))}
      </ul>
      <Result error={edit.error ?? remove.error} />
    </Section>
  );
}

function Reset({ campaignId, member }: { campaignId: string; member: AssistantTestMemberDto }) {
  const [parts, setParts] = useState<Record<ResetPart, boolean>>({ chat: true, notes: false, wakeups: true, preferences: false });
  const [armed, setArmed] = useState(false);
  const { mutate, data, error, isPending } = useAssistantTestReset();
  const labels: Record<ResetPart, string> = {
    chat: `Chat (${member.messages} messages, summaries, thumbs, open proposals)`,
    notes: `Notes (${member.notes.length})`,
    wakeups: `Pending wake-ups and events (${member.pendingWakeups.length} wake-ups)`,
    preferences: 'Preferences (back to the defaults)',
  };
  return (
    <Section title="Reset">
      <p className="text-xs text-muted-foreground">Runs and their telemetry stay. This can't be undone.</p>
      <div className="space-y-2">
        {RESET_PARTS.map((part) => (
          <label key={part} className="flex items-center gap-2 text-sm">
            <Checkbox checked={parts[part]} onCheckedChange={(v) => setParts({ ...parts, [part]: v === true })} />
            {labels[part]}
          </label>
        ))}
      </div>
      <div className="flex gap-2">
        {armed ? (
          <>
            <Button
              size="sm"
              variant="destructive"
              disabled={isPending}
              onClick={() => mutate({ campaignId, userId: member.userId, ...parts }, { onSettled: () => setArmed(false) })}
            >
              Yes, wipe {member.name || 'this member'}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setArmed(false)}>
              Cancel
            </Button>
          </>
        ) : (
          <Button size="sm" variant="outline" disabled={!Object.values(parts).some(Boolean)} onClick={() => setArmed(true)}>
            Reset selected
          </Button>
        )}
      </div>
      <Result error={error}>{data && `Removed ${data.removed} rows. Reopen Scout on the phone to see it fresh.`}</Result>
    </Section>
  );
}

/** The platform admin's bench for Scout's background side, one campaign and one member at a time. */
export function AssistantTestBench() {
  const [campaignId, setCampaignId] = useState<string>();
  const [userId, setUserId] = useState<string>();
  const campaigns = useAssistantTestCampaigns();
  const members = useAssistantTestMembers(campaignId);
  const member = members.data?.find((m) => m.userId === userId);

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Scout test bench</h1>
        <p className="text-muted-foreground mt-1">
          Run the brief, fire wake-ups, send pushes and reset a member's Scout state for testing. Only campaigns with Scout
          on, and only members Scout admits. <Link to="/admin/assistant-telemetry" className="underline">Back to telemetry</Link>
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <FilterSelect
          label="Campaign"
          value={campaignId}
          onChange={(v) => {
            setCampaignId(v);
            setUserId(undefined);
          }}
          options={(campaigns.data ?? []).map((c) => ({ value: c.id, label: c.name }))}
        />
        {campaignId && (
          <FilterSelect
            label="Member"
            value={userId}
            onChange={setUserId}
            options={(members.data ?? []).map((m) => ({ value: m.userId, label: `${m.name || m.email} (${m.isPlanner ? 'Planner' : 'Caller'})` }))}
          />
        )}
        {(campaigns.isLoading || members.isLoading) && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
      </div>
      {campaigns.data?.length === 0 && <p className="text-sm text-muted-foreground">No campaign has Scout on in this subsidiary.</p>}

      {campaignId && member && (
        <div className="space-y-4">
          <Purchase campaignId={campaignId} member={member} />
          <Runs campaignId={campaignId} member={member} />
          <Push campaignId={campaignId} member={member} />
          <Notes campaignId={campaignId} member={member} />
          <Reset campaignId={campaignId} member={member} />
        </div>
      )}
    </div>
  );
}
